import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { orderAPI } from "../../services/api";
import type { Order, OrdersState } from "../../types/orders";

const initialState: OrdersState = {
  orders: [],
  current: null,
  loading: false,
  saving: false,
  error: null,
  pagination: { page: 1, limit: 20, total: 0, pages: 0 },
};

const err = (e: any, fallback: string) =>
  e.response?.data?.message || fallback;

export const fetchOrders = createAsyncThunk(
  "orders/list",
  async (params: any, { rejectWithValue }) => {
    try {
      const res = await orderAPI.list(params);
      return res.data.data;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to load orders"));
    }
  },
);

export const fetchOrder = createAsyncThunk(
  "orders/get",
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await orderAPI.getOne(id);
      return res.data.data.order as Order;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to load order"));
    }
  },
);

export const updateOrder = createAsyncThunk(
  "orders/update",
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const res = await orderAPI.update(id, data);
      return res.data.data.order as Order;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to update order"));
    }
  },
);

export const refreshOrder = createAsyncThunk(
  "orders/refresh",
  async (
    { id, confirmed }: { id: string; confirmed?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const res = await orderAPI.refresh(id, confirmed);
      return res.data.data.order as Order;
    } catch (e: any) {
      // 409 SHIPMENT_LINKED is handled by the caller via unwrap()
      return rejectWithValue({
        message: err(e, "Failed to refresh order"),
        code: e.response?.data?.code,
      });
    }
  },
);

export const setOrderAutoSync = createAsyncThunk(
  "orders/autoSync",
  async (
    { id, enabled }: { id: string; enabled: boolean },
    { rejectWithValue },
  ) => {
    try {
      const res = await orderAPI.setAutoSync(id, enabled);
      return res.data.data.order as Order;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to toggle auto-sync"));
    }
  },
);

export const cancelOrder = createAsyncThunk(
  "orders/cancel",
  async (
    { id, confirmed }: { id: string; confirmed?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const res = await orderAPI.cancel(id, confirmed);
      return res.data.data.order as Order;
    } catch (e: any) {
      return rejectWithValue({
        message: err(e, "Failed to cancel order"),
        code: e.response?.data?.code,
      });
    }
  },
);

export const dismissOrderDiscrepancy = createAsyncThunk(
  "orders/dismissDiscrepancy",
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await orderAPI.dismissDiscrepancy(id);
      return res.data.data.order as Order;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to dismiss discrepancy"));
    }
  },
);

/** Replace an order in the list (and current) after a mutation. */
const applyOrder = (state: OrdersState, order: Order) => {
  const idx = state.orders.findIndex((o) => o._id === order._id);
  if (idx >= 0) {
    // List rows are fetched without items — keep the row light
    const { items: _items, ...rest } = order;
    state.orders[idx] = { ...state.orders[idx], ...rest };
  }
  if (state.current?._id === order._id) {
    state.current = { ...state.current, ...order };
  }
};

const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    clearOrdersError: (state) => {
      state.error = null;
    },
    clearCurrentOrder: (state) => {
      state.current = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.orders;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload;
      })
      .addCase(fetchOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    for (const thunk of [
      updateOrder,
      refreshOrder,
      setOrderAutoSync,
      cancelOrder,
      dismissOrderDiscrepancy,
    ]) {
      builder
        .addCase(thunk.pending, (state) => {
          state.saving = true;
          state.error = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.saving = false;
          if (action.payload) applyOrder(state, action.payload as Order);
        })
        .addCase(thunk.rejected, (state, action) => {
          state.saving = false;
          const payload = action.payload as any;
          // SHIPMENT_LINKED 409s are confirmation prompts, not errors
          if (payload?.code !== "SHIPMENT_LINKED") {
            state.error = payload?.message ?? (payload as string);
          }
        });
    }
  },
});

export const { clearOrdersError, clearCurrentOrder } = ordersSlice.actions;
export default ordersSlice.reducer;
